/**
 * Generic page stack state primitives.
 *
 * This module is intentionally domain-agnostic: callers supply their own
 * activity IDs and page shape, and this module provides immutable state
 * transitions plus selectors.
 */
export interface PageStackActivityState<Page> {
  stack: Page[];
}

export interface PageStackState<ActivityId extends string, Page> {
  activeActivity: ActivityId;
  activities: Record<ActivityId, PageStackActivityState<Page>>;
}

export type PageStackAction<ActivityId extends string, Page> =
  | {
      type: 'switch-activity';
      activityId: ActivityId;
    }
  | {
      type: 'push-page';
      activityId: ActivityId;
      page: Page;
    }
  | {
      type: 'replace-current-page';
      activityId: ActivityId;
      page: Page;
    }
  | {
      type: 'go-back';
      activityId: ActivityId;
    }
  | {
      type: 'reset-activity-stack';
      activityId: ActivityId;
      rootPage?: Page;
    };

interface CreateInitialPageStackStateOptions<ActivityId extends string, Page> {
  activityIds: ActivityId[];
  initialActivity: ActivityId;
  buildDefaultRootPage: (activityId: ActivityId) => Page;
}

function getActivityState<ActivityId extends string, Page>(
  state: PageStackState<ActivityId, Page>,
  activityId: ActivityId,
): PageStackActivityState<Page> {
  return state.activities[activityId];
}

function updateActivityState<ActivityId extends string, Page>(
  state: PageStackState<ActivityId, Page>,
  activityId: ActivityId,
  nextActivityState: PageStackActivityState<Page>,
): PageStackState<ActivityId, Page> {
  return {
    ...state,
    activities: {
      ...state.activities,
      [activityId]: nextActivityState,
    },
  };
}

/**
 * Creates initial state with one root page per activity.
 */
export function createInitialPageStackState<ActivityId extends string, Page>({
  activityIds,
  initialActivity,
  buildDefaultRootPage,
}: CreateInitialPageStackStateOptions<ActivityId, Page>): PageStackState<
  ActivityId,
  Page
> {
  // Build one root page per activity so each activity keeps independent state.
  const activities = {} as Record<ActivityId, PageStackActivityState<Page>>;

  for (const activityId of activityIds) {
    activities[activityId] = {
      stack: [buildDefaultRootPage(activityId)],
    };
  }

  return {
    activeActivity: initialActivity,
    activities,
  };
}

/**
 * Reducer for immutable page-stack transitions.
 *
 * `switch-activity` changes focus only.
 * Other actions mutate only the targeted activity stack.
 */
export function reducePageStackState<ActivityId extends string, Page>(
  state: PageStackState<ActivityId, Page>,
  action: PageStackAction<ActivityId, Page>,
  buildDefaultRootPage: (activityId: ActivityId) => Page,
): PageStackState<ActivityId, Page> {
  switch (action.type) {
    case 'switch-activity':
      if (state.activeActivity === action.activityId) {
        return state;
      }

      return {
        ...state,
        activeActivity: action.activityId,
      };

    case 'push-page': {
      const activityState = getActivityState(state, action.activityId);
      return updateActivityState(state, action.activityId, {
        stack: [...activityState.stack, action.page],
      });
    }

    case 'replace-current-page': {
      const activityState = getActivityState(state, action.activityId);
      const stackWithoutCurrent = activityState.stack.slice(0, -1);

      return updateActivityState(state, action.activityId, {
        stack: [...stackWithoutCurrent, action.page],
      });
    }

    case 'go-back': {
      const activityState = getActivityState(state, action.activityId);

      if (activityState.stack.length <= 1) {
        return state;
      }

      return updateActivityState(state, action.activityId, {
        stack: activityState.stack.slice(0, -1),
      });
    }

    case 'reset-activity-stack': {
      const rootPage =
        action.rootPage ?? buildDefaultRootPage(action.activityId);

      return updateActivityState(state, action.activityId, {
        stack: [rootPage],
      });
    }

    default:
      return state;
  }
}

/**
 * Returns the current page for an activity, defaulting to active activity.
 */
export function getCurrentPage<ActivityId extends string, Page>(
  state: PageStackState<ActivityId, Page>,
  activityId: ActivityId = state.activeActivity,
): Page {
  const stack = state.activities[activityId].stack;
  return stack[stack.length - 1];
}

/**
 * Returns the full page stack for an activity, defaulting to active activity.
 */
export function getPageStack<ActivityId extends string, Page>(
  state: PageStackState<ActivityId, Page>,
  activityId: ActivityId = state.activeActivity,
): Page[] {
  return state.activities[activityId].stack;
}

/**
 * Indicates whether the activity has a previous page to navigate back to.
 */
export function canGoBack<ActivityId extends string, Page>(
  state: PageStackState<ActivityId, Page>,
  activityId: ActivityId = state.activeActivity,
): boolean {
  return getPageStack(state, activityId).length > 1;
}
